import clsx from 'clsx'
import Spinner from 'common/components/Spinner'
import {calculateErrorMessage} from 'common/helpers'
import {useFormik} from 'formik'
import {useState} from 'react'
import {useDispatch} from 'react-redux'
import {Link} from 'react-router'
import {toast} from 'react-toastify'
import {forgotPassword} from 'setup/redux/Actions/SignIn/forgotPassword'
import * as Yup from 'yup'

const initialValues = {
  email: '',
  resend: false,
}

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string().email().min(3).max(50).required().label('Email'),
  resend: Yup.boolean().label('Resend'),
})

export function LegacyForgotPasswordNew() {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [alreadySent, setAlreadySent] = useState<boolean>(false)

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, {setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)

      const onSuccess = () => {
        setHasErrors(false)
        setLoading(false)
        toast.success('Forgot password email sent')
      }

      const onFailure = (err: any) => {
        setHasErrors(true)
        setLoading(false)
        setSubmitting(false)
        const message = calculateErrorMessage(err, 'Error requesting password reset')
        if (message === 'An email has already been sent to your account.') setAlreadySent(true)
        toast.error(message)
      }

      dispatch(forgotPassword(values.email, values.resend, onSuccess, onFailure))
    },
  })

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        <h1 className='text-gray-900 fw-bolder mb-3'>Forgot Password ?</h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          Enter your email to reset your password.
        </div>
      </div>

      {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>
            Sorry, looks like there are some errors detected, please try again.
          </div>
        </div>
      )}

      {hasErrors === false && (
        <div className='mb-10 bg-light-info p-8 rounded'>
          <div className='text-info'>Sent password reset. Please check your email</div>
        </div>
      )}

      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-gray-900 fs-6'>Email</label>
        <input
          type='email'
          placeholder=''
          autoComplete='off'
          {...formik.getFieldProps('email')}
          className={clsx('form-control bg-transparent', {
            'is-invalid': formik.touched.email && formik.errors.email,
          })}
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>

      {alreadySent && (
        <div className='fv-row mb-8'>
          <label className='form-check-label fw-bolder text-gray-900 fs-6'>Resend Email</label>
          <input
            type='checkbox'
            {...formik.getFieldProps('resend')}
            className={clsx('form-check-input', {
              'is-invalid': formik.touched.resend && formik.errors.resend,
            })}
          />
          {formik.touched.resend && formik.errors.resend && (
            <div className='invalid-feedback'>{formik.errors.resend}</div>
          )}
        </div>
      )}

      <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
        <button type='submit' id='kt_password_reset_submit' className='btn btn-primary me-4'>
          {loading ? <Spinner size='sm' /> : <span className='indicator-label'>Submit</span>}
        </button>
        <Link to='/auth/login'>
          <button
            type='button'
            id='kt_login_password_reset_form_cancel_button'
            className='btn btn-light'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            Cancel
          </button>
        </Link>
      </div>
    </form>
  )
}
